

import { format, isThisWeek, isToday, isYesterday } from "date-fns";
import DOMPurify from "dompurify";
import { jwtDecode } from "jwt-decode";

import { API_URL } from "./constant";

export const isNumeric = (num: any) => (typeof num === "number" || (typeof num === "string" && num.trim() !== "")) && !Number.isNaN(num as number);

export const formatKB = (kBytes: any, decimals = 2) => {
  const bytes = kBytes;
  if (!Number(bytes)) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

export const ResolveClientUrl = (url: string | undefined) => {
  const baseURL = import.meta.env.VITE_API_URL as string;
  // fix  duplicate forward slashes from the URL
  return `${baseURL}${url ?? ""}`.replace(/(https?:\/\/)|(\/)+/g, "$1$2").replace("/api/api/", "/api/");
};

export function DownloadAttachment(Path: string): any {
  if (!Path || Path === "") return;

  return `${API_URL}${Path}`;
}

export function FormatInputDate(dateString: string | undefined) {
  return dateString?.slice(0, 10);
}

export const FormatInputCurrency = (value: string | null | undefined) => {
  if (value === undefined || value === null) {
    return "";
  }
  const numericValue = typeof value === "string" ? parseFloat(value) : value;
  if (Number.isNaN(numericValue)) {
    return String(value);
  }
  const formattedValue = numericValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return formattedValue.slice(0, -3);
};

export const getFileExtension = (fileName: string) => {
  if (!fileName || fileName === "" || fileName.length === 0) return;
  const parts = fileName.split(".");
  if (parts.length > 1) {
    return `${parts[parts.length - 1]}`;
  }
  return ""; // No extension found
};

export function capitalizeTxt(txt: string | null | undefined) {
  if (txt === undefined || txt === null || txt === "") return;
  return `${txt.charAt(0).toUpperCase()}${txt.slice(1)}`;
}

export function getAvatarNameByEmail(email: string | undefined) {
  if (!email) return;
  let namePart = email.split("@")[0];
  if (namePart.startsWith("con-")) {
    namePart = namePart.substring(4);
  }
  if (namePart.includes(" ")) {
    const parts = email.split(" ");
    let placeholderName = "";
    // parts.forEach(part => (part[0] ? (placeholderName += part[0].toUpperCase()) : (placeholderName += "")));
    placeholderName += parts[0][0].toUpperCase();
    placeholderName += parts[1] ? parts[1][0].toUpperCase() : "";

    return placeholderName;
  }
  const firstNameInitial = namePart.charAt(0).toUpperCase();
  const lastNameInitial = namePart.charAt(1).toUpperCase();

  return firstNameInitial + lastNameInitial;
}

const ALLOWED_PROTOCOLS = new Set(["http:", "https:"]);

function _isSafeHref(href: string) {
  try {
    const u = new URL(href, window.location.origin);
    return ALLOWED_PROTOCOLS.has(u.protocol);
  } catch {
    return false;
  }
}

export const SafeHtml = (html: string, ClassName = "") => {
  const input = (html ?? "").toString();

  const sanitized = DOMPurify.sanitize(input, {
    ALLOW_UNKNOWN_PROTOCOLS: false,
    FORBID_ATTR: ["style", "srcset"],
    FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "link", "meta"],
    USE_PROFILES: { html: true },
  });

  const cleaned = sanitized.replace(/<p>\s*(<br\s*\/?>|\s)*\s*<\/p>/gi, "");

  const linked = DOMPurify.sanitize(linkify(cleaned), {
    USE_PROFILES: { html: true },
  });

  const finalHtml = DOMPurify.sanitize(linked, {
    ALLOW_UNKNOWN_PROTOCOLS: false,
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "style"],
    FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "link", "meta"],
    USE_PROFILES: { html: true },
  });

  const dir = getTextDirection(finalHtml);

  return (
    <div
      className={`prose text-1.6 text-secondary-400 prose-p:m-0 prose-li:leading-tight prose-img:!h-60 ${ClassName}`}
      dangerouslySetInnerHTML={{
        __html: finalHtml.replace(/<a\s/gi, '<a target="_blank" rel="noopener noreferrer nofollow" '),
      }}
      dir={dir}
    />
  );
};

export function linkify(text: string) {
  const urlPattern = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gi;
  let textUpdate = text.replace(urlPattern, (url: string) => {
    const isInsideTag = /<[^>]+?=[^>]/gi.test(text);
    if (!isInsideTag) {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-sky-600">${url}</a>`;
    }
    return url;
  });
  textUpdate = textUpdate.replace(/<a(?![^>]*\btarget=)[^>]*?>/g, match => match.replace("<a ", '<a target="_blank" class="text-sky-600" '));
  return textUpdate;
}

export function stripHTML(htmlString: string) {
  return htmlString.replace(/<[^>]*>/g, "");
}

export const base64url = (string: string) => String(string.replace(/\\/g, "-").replace(/\//g, "_").replace(/=+$/, ""));

export const base64regular = (string: string) => String(string.replace(/-/g, "\\").replace(/_/g, "/"));

export const getTextDirection = (text: string) => {
  const cleanText = stripHTML(text);
  // check arabic letters
  const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
  // console.log(arabicRegex.test(cleanText) ? "rtl" : "ltr", cleanText);
  return arabicRegex.test(cleanText) ? "rtl" : "ltr";
};

export function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export const formatTimestamp = (timestamp: number | Date) => {
  const date = new Date(timestamp);

  if (isToday(date)) {
    return `${format(date, "h:mm aa")}`;
  }
  if (isYesterday(date)) {
    return `Yesterday`;
  }
  if (isThisWeek(date)) {
    return `${format(date, "EEEE")}`; // Day of the week with time
  }
  return format(date, "dd/MM"); // Date and time format
};

export const checkTokenIsValid = token => {
  if (token) {
    const decodedToken = jwtDecode(token);
    const expirationDate = new Date((decodedToken as any).exp * 1000);
    const currentDate = new Date();
    return currentDate < expirationDate;
  }
  return false;
};

export const formatShortNumber = num => {
  const n = Number(num);
  if (!Number.isFinite(n)) return "0";

  const abs = Math.abs(n);
  const truncate = (val, div) => Math.floor((val / div) * 10) / 10;

  if (abs >= 1_000_000_000) return `${truncate(n, 1_000_000_000)}B`;
  if (abs >= 1_000_000) return `${truncate(n, 1_000_000)}M`;
  if (abs >= 1_000) return `${truncate(n, 1_000)}K`;

  return n.toString();
};
